import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  signInWithPopup,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  User as FirebaseUser,
} from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";

import {
  getFirebaseAuth,
  getFirebaseDB,
  getFirebaseGoogleProvider,
  getFirebaseFunctions,
} from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { useUIStore } from "@/store/use-ui-store";

// Enhanced User type with Firebase integration
export type User = {
  id: string;
  name: string | null;
  email: string | null;
  emailVerified: boolean;
  image?: string;
  phoneNumber?: string;
  // Role from Firebase Custom Claims (SECURE - server-side only)
  // Set via: node scripts/check-user-role.js <email> <role>
  role?: "landlord" | "tenant" | "admin" | "super-admin";
  createdAt: string;
  lastLoginAt: string;
};

// Firebase Auth Context Type
type AuthContextType = {
  user: User | null;
  status: "loading" | "authenticated" | "unauthenticated";
  // Email/Password methods
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    role?: "tenant" | "landlord" | "admin" | "super-admin"
  ) => Promise<void>;
  // Social sign-in
  signInWithGoogle: () => Promise<void>;
  // Sign out
  signOut: () => Promise<void>;
  // Password reset
  resetPassword: (email: string) => Promise<void>;
  // Utility methods
  refreshUser: () => Promise<void>;
  updateUserProfile: (updates: Partial<User>) => Promise<void>;
  // Error handling
  error: AuthError | null;
  clearError: () => void;
};

type AuthError = {
  code: string;
  message: string;
};

const AuthContext = createContext<AuthContextType | null>(null);

// Auth Provider Component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<
    "loading" | "authenticated" | "unauthenticated"
  >("loading");
  const [error, setError] = useState<AuthError | null>(null);

  const { addNotification, setLoading } = useUIStore();

  // Aggressive auth caching (5 minutes)
  const CACHE_KEY = "homespaces_auth_cache";
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const getCachedAuth = () => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { user, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          return user;
        }
      }
    } catch (e) {
      // Silently handle cache errors
    }
    return null;
  };

  const setAuthCache = (user: User | null) => {
    try {
      const cacheData = {
        user,
        timestamp: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (e) {
      // Silently handle cache errors
    }
  };

  // Clear error helper
  const clearError = useCallback(() => setError(null), []);

  // Convert Firebase user to our User type
  const convertFirebaseUser = useCallback(
    async (firebaseUser: FirebaseUser): Promise<User> => {
      try {
        // Get the ID token to access custom claims (THIS IS SECURE - claims are set server-side)
        const idTokenResult = await firebaseUser.getIdTokenResult();
        const customClaims = idTokenResult.claims;

        // Extract role from custom claims (server-side set, cannot be modified by client)
        const role =
          (customClaims.role as
            | "landlord"
            | "tenant"
            | "admin"
            | "super-admin") || "tenant";

        console.log("🔐 User custom claims:", customClaims);
        console.log("👤 User role from claims:", role);

        // Try to get user document from Firestore for profile data (NOT for role)
        const userDocRef = doc(getFirebaseDB()!, "users", firebaseUser.uid);
        const userDoc = await getDoc(userDocRef);

        let userData: Partial<User> = {};

        if (userDoc.exists()) {
          userData = userDoc.data() as Partial<User>;
        } else {
          // Create user document if it doesn't exist (profile data only)
          const newUserData = {
            name:
              firebaseUser.displayName ||
              firebaseUser.email?.split("@")[0] ||
              "User",
            email: firebaseUser.email,
            emailVerified: firebaseUser.emailVerified,
            createdAt: new Date().toISOString(),
            lastLoginAt: new Date().toISOString(),
            ...(firebaseUser.photoURL && { image: firebaseUser.photoURL }),
            ...(firebaseUser.phoneNumber && {
              phoneNumber: firebaseUser.phoneNumber,
            }),
          };

          await setDoc(userDocRef, newUserData);
          userData = newUserData;
        }

        return {
          id: firebaseUser.uid,
          name: firebaseUser.displayName || userData.name || null,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified,
          image: firebaseUser.photoURL || userData.image,
          phoneNumber: firebaseUser.phoneNumber || userData.phoneNumber,
          role, // Role from custom claims (SECURE)
          createdAt: userData.createdAt || new Date().toISOString(),
          lastLoginAt: new Date().toISOString(),
        };
      } catch (error) {
        console.error("Error fetching user data:", error);

        // Fallback to basic Firebase user data with default tenant role
        return {
          id: firebaseUser.uid,
          name:
            firebaseUser.displayName ||
            firebaseUser.email?.split("@")[0] ||
            "User",
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified,
          image: firebaseUser.photoURL || undefined,
          role: "tenant", // Default role if claims are not set
          createdAt: new Date().toISOString(),
          lastLoginAt: new Date().toISOString(),
        };
      }
    },
    []
  );

  // Handle auth errors
  const handleAuthError = useCallback((error: any) => {
    console.error("Auth error:", error);

    let errorMessage = "An unexpected error occurred. Please try again.";

    switch (error.code) {
      case "auth/user-not-found":
        errorMessage = "No account found with this email address.";
        break;
      case "auth/wrong-password":
        errorMessage = "Incorrect password.";
        break;
      case "auth/email-already-in-use":
        errorMessage = "An account with this email already exists.";
        break;
      case "auth/weak-password":
        errorMessage = "Password must be at least 6 characters.";
        break;
      case "auth/invalid-email":
        errorMessage = "Please enter a valid email address.";
        break;
      case "auth/network-request-failed":
        errorMessage = "Network error. Please check your connection.";
        break;
      case "auth/too-many-requests":
        errorMessage = "Too many attempts. Please try again later.";
        break;
      default:
        errorMessage = error.message || "An unexpected error occurred.";
    }

    setError({ code: error.code, message: errorMessage });

    // Show error toast
    toast.error("Authentication Error", {
      description: errorMessage,
    });
  }, []);

  // Sign in with email and password
  const signIn = async (email: string, password: string): Promise<void> => {
    setLoading(true);
    clearError();

    try {
      const auth = getFirebaseAuth();
      if (!auth) throw new Error("Firebase Auth not available");

      const result = await signInWithEmailAndPassword(auth, email, password);

      // Update last login in Firestore
      if (result.user) {
        const firestore = getFirebaseDB();
        if (firestore) {
          const userDocRef = doc(firestore, "users", result.user.uid);
          await updateDoc(userDocRef, {
            lastLoginAt: new Date().toISOString(),
          });
        }
      }

      toast.success("Welcome back!", {
        description: "You have been signed in successfully.",
      });

      // Navigate based on user role
      const convertedUser = await convertFirebaseUser(result.user);
      if (convertedUser.role === "landlord") {
        navigate("/landlord");
      } else if (convertedUser.role === "tenant") {
        navigate("/tenant");
      } else if (convertedUser.role === "admin") {
        navigate("/admin/properties");
      } else {
        navigate("/");
      }
    } catch (error: any) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign up with email and password
  const signUp = async (
    email: string,
    password: string,
    role: "tenant" | "landlord" | "admin" | "super-admin" = "tenant"
  ): Promise<void> => {
    setLoading(true);
    clearError();

    try {
      const auth = getFirebaseAuth();
      if (!auth) throw new Error("Firebase Auth not available");

      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Call Cloud Function to set custom claims (SECURE - server-side only)
      try {
        const functions = getFirebaseFunctions();
        if (functions) {
          const setUserRoleFunc = httpsCallable(functions, "setUserRole");
          await setUserRoleFunc({ role });

          console.log("✅ Role set via custom claims:", role);

          // Force token refresh to get new claims
          await result.user.getIdToken(true);
        }
      } catch (roleError) {
        console.error("Failed to set user role via Cloud Function:", roleError);
        // Don't fail signup if role setting fails
        // User can set it later or admin can set it
        toast.warning("Account created, but role not set", {
          description:
            "Please contact support to activate your account features.",
        });
      }

      // Create user profile document (profile data only, NO role)
      const db = getFirebaseDB();
      if (db) {
        // Check if a Firestore user with this email already exists
        const usersRef = collection(db, "users");
        const emailQuery = query(
          usersRef,
          where("email", "==", result.user.email)
        );
        const existingUsers = await getDocs(emailQuery);

        if (!existingUsers.empty) {
          // A Firestore user with this email already exists
          // This shouldn't happen if Firebase Auth is working correctly
          const existingUserId = existingUsers.docs[0].id;

          if (existingUserId !== result.user.uid) {
            // Different user ID means duplicate - this is a critical error
            console.error("DUPLICATE EMAIL DETECTED:", {
              email: result.user.email,
              newAuthUid: result.user.uid,
              existingFirestoreUid: existingUserId,
            });

            toast.error("Account Already Exists", {
              description:
                "An account with this email already exists. Please contact support if you need help accessing your account.",
            });

            // Delete the Firebase Auth user we just created to prevent orphan
            await result.user.delete();
            throw new Error("Duplicate email detected in Firestore");
          }
          // If same UID, user document already exists (idempotent - safe to skip)
          console.log("User document already exists, skipping creation");
        } else {
          // No existing user - safe to create
          await setDoc(doc(db, "users", result.user.uid), {
            email: result.user.email,
            name: email.split("@")[0],
            emailVerified: result.user.emailVerified,
            createdAt: new Date().toISOString(),
            lastLoginAt: new Date().toISOString(),
            // NOTE: Role is NOT stored here - it's in custom claims (secure)
          });
        }
      }

      // Link pending leases if this is a tenant signup
      if (role === "tenant" && result.user.email) {
        try {
          const { linkPendingLeasesToTenant } = await import(
            "@/lib/lease-service"
          );
          const linkedCount = await linkPendingLeasesToTenant(
            result.user.email,
            result.user.uid
          );

          if (linkedCount > 0) {
            console.log(`✅ Linked ${linkedCount} pending lease(s) to tenant`);
            toast.success("Welcome!", {
              description: `Your account has been created and ${linkedCount} lease agreement(s) are ready for your review.`,
            });
          } else {
            toast.success("Account Created!", {
              description:
                "Your account has been created successfully. You are now signed in.",
            });
          }
        } catch (linkError) {
          console.error("Failed to link pending leases:", linkError);
          // Don't fail signup if lease linking fails
          toast.success("Account Created!", {
            description:
              "Your account has been created successfully. You are now signed in.",
          });
        }
      } else {
        toast.success("Account Created!", {
          description:
            "Your account has been created successfully. You are now signed in.",
        });
      }

      // Refresh auth state to get the new role from claims
      await refreshUser();

      // Navigate based on selected role
      if (role === "landlord") {
        navigate("/landlord");
      } else {
        navigate("/tenant");
      }
    } catch (error: any) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Google
  const signInWithGoogle = async (): Promise<void> => {
    setLoading(true);
    clearError();

    try {
      const auth = getFirebaseAuth();
      const googleProvider = getFirebaseGoogleProvider();
      if (!auth || !googleProvider)
        throw new Error("Firebase Auth not available");

      const result = await signInWithPopup(auth, googleProvider);

      if (result.user) {
        await convertFirebaseUser(result.user);

        const convertedUser = await convertFirebaseUser(result.user);

        toast.success("Welcome!", {
          description: "You have been signed in with Google.",
        });

        // Navigate based on user role
        if (convertedUser.role === "landlord") {
          navigate("/landlord");
        } else if (convertedUser.role === "tenant") {
          navigate("/tenant");
        } else if (convertedUser.role === "admin") {
          navigate("/admin/properties");
        } else {
          navigate("/");
        }
      }
    } catch (error: any) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async (): Promise<void> => {
    setLoading(true);

    try {
      const auth = getFirebaseAuth();
      if (auth) {
        await firebaseSignOut(auth);
      }

      addNotification({
        type: "info",
        title: "Signed Out",
        message: "You have been signed out successfully.",
      });
    } catch (error: any) {
      handleAuthError(error);
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (email: string): Promise<void> => {
    setLoading(true);
    clearError();

    try {
      const auth = getFirebaseAuth();
      if (!auth) throw new Error("Firebase Auth not available");

      await sendPasswordResetEmail(auth, email);

      addNotification({
        type: "success",
        title: "Password Reset Email Sent",
        message: "Please check your email for password reset instructions.",
      });
    } catch (error: any) {
      handleAuthError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Refresh current user data
  const refreshUser = async (): Promise<void> => {
    const auth = getFirebaseAuth();
    if (auth && auth.currentUser) {
      setUser(await convertFirebaseUser(auth.currentUser));
    }
  };

  // Update user profile (SAFE - blocks role/security fields)
  const updateUserProfile = async (updates: Partial<User>): Promise<void> => {
    const auth = getFirebaseAuth();
    if (!auth || !auth.currentUser) throw new Error("No authenticated user");

    try {
      const firestore = getFirebaseDB();
      if (!firestore) throw new Error("Firebase DB not available");

      // SECURITY: Remove sensitive fields that users should NOT be able to update
      const { role, id, emailVerified, ...safeUpdates } = updates;

      // Only allow updating these specific safe fields
      const allowedFields: Partial<User> = {};
      if (safeUpdates.name !== undefined) allowedFields.name = safeUpdates.name;
      if (safeUpdates.phoneNumber !== undefined)
        allowedFields.phoneNumber = safeUpdates.phoneNumber;
      if (safeUpdates.image !== undefined)
        allowedFields.image = safeUpdates.image;

      if (Object.keys(allowedFields).length === 0) {
        throw new Error("No valid fields to update");
      }

      const userDocRef = doc(firestore, "users", auth.currentUser.uid);
      await updateDoc(userDocRef, {
        ...allowedFields,
        lastUpdated: new Date().toISOString(),
      });
      await refreshUser();

      addNotification({
        type: "success",
        title: "Profile Updated",
        message: "Your profile has been updated successfully.",
      });
    } catch (error: any) {
      handleAuthError(error);
      throw error;
    }
  };

  // Listen to authentication state changes with cache-first approach
  useEffect(() => {
    // 1. Try cache-first loading (instant!)
    const cachedUser = getCachedAuth();

    if (cachedUser) {
      setUser(cachedUser);
      setStatus("authenticated"); // ✅ No loading for cached users!

      // 2. Background refresh auth (non-blocking)
      // This happens silently without showing loading
      const auth = getFirebaseAuth();
      if (!auth) return;

      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          try {
            const convertedUser = await convertFirebaseUser(firebaseUser);
            // Only update state if different from cache
            if (JSON.stringify(convertedUser) !== JSON.stringify(cachedUser)) {
              setUser(convertedUser);
              setAuthCache(convertedUser); // Update cache
            }
          } catch (error) {
            console.error("Error converting Firebase user:", error);
            // Keep cached user on error
          }
        } else {
          // Handle logout case
          setUser(null);
          setStatus("unauthenticated");
          setAuthCache(null);
        }
      });

      return unsubscribe;
    }

    // 3. Fresh load for non-cached users
    setStatus("loading");

    const auth = getFirebaseAuth();
    if (!auth) return;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const convertedUser = await convertFirebaseUser(firebaseUser);
          setUser(convertedUser);
          setStatus("authenticated");
          setAuthCache(convertedUser); // Cache for future use
        } catch (error) {
          console.error("Error converting Firebase user:", error);
          setUser(null);
          setStatus("unauthenticated");
        }
      } else {
        setUser(null);
        setStatus("unauthenticated");
        setAuthCache(null);
      }
    });

    return unsubscribe;
  }, [convertFirebaseUser]);

  const contextValue: AuthContextType = {
    user,
    status,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    resetPassword,
    refreshUser,
    updateUserProfile,
    error,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
